'use client';

import Link from "next/link";
import { useEffect, useState } from "react";
import ChevronRight from '@/assets/svg/icons/chevron-right.svg';
import MapPin from '@/assets/svg/icons/map-pin.svg';
import CustomSVG from "../common/svg";
import { CheckScroll } from "@/functions/check_scroll";
import classes from "./public_provider_page.module.css";
import PublicProviderPageFavourite from "./public_provider_page_favourite";
import { usePublicProviderInteraction } from "./public_provider_interaction_context";

export default function PublicProviderHeaderData({ provider, is_preview, deleted }) {
    const [isScrolled, setIsScrolled] = useState(0);
    const [isCompact, setIsCompact] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { openContactMobile, contactMobile } = usePublicProviderInteraction();

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 902);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    CheckScroll(setIsScrolled, true);
                    ticking = false;
                });
                ticking = true;
            }
        };

        CheckScroll(setIsScrolled, true);
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const compactThreshold = isMobile ? 140 : 250;
        const expandThreshold = isMobile ? 120 : 150;

        if (isMobile && contactMobile) {
            if (!isCompact) setIsCompact(true);
            return;
        }

        if (isScrolled > compactThreshold && !isCompact) {
            setIsCompact(true);
        } else if (isScrolled < expandThreshold && isCompact) {
            setIsCompact(false);
        }
    }, [contactMobile, isCompact, isMobile, isScrolled]);

    if (!provider) return null;

    const showCompact = !is_preview && isMobile && (contactMobile || isCompact);

    const content = (
        <div className={`${classes["public-provider-page-main-header"]} ${is_preview ? classes["preview"] : ""}`}>
            <div className="d-flex flex-column align-items-center align-self-stretch gap-4 flex-grow-1">
                {(is_preview || isMobile || isScrolled <= 50) && <nav aria-label="Morzsanavigáció" className={classes["provider-breadcrumb"]}>
                    {!is_preview && <>
                        {provider.type && provider.url && <Link href={`/${provider.url.split("/")[0]}`} title={provider.type.name} className={`${classes["header-link-item"]}`}>
                            <p className="margin-0 color-gray-500 text-sm-medium">{provider.type.name}</p>
                        </Link>}
                        {provider.type && provider.url && <CustomSVG className="stroke-gray-500 flex-shrink-0" SvgFunction={ChevronRight} height={16} width={16} />}
                        {provider.address && provider.url && <Link href={`/${provider.url.split("/")[0]}/${provider.url.split("/")[1]}`} title={provider.address.city} className={`${classes["header-link-item"]}`}>
                            <p className="margin-0 color-gray-500 text-sm-medium">{provider.address.city}</p>
                        </Link>}
                        <div className={classes["provider-breadcrumb-current"]}>
                            {provider.address && provider.url && <CustomSVG className="stroke-gray-500 flex-shrink-0" SvgFunction={ChevronRight} height={16} width={16} />}
                            <Link href={`/${provider.url}`} className={`${classes["header-link-item"]} margin-0`} title={provider.name} aria-current="page">
                                <p className="margin-0 color-gray-900 text-sm-semibold">{provider.name}</p>
                            </Link>
                        </div>
                    </>}
                </nav>}
                <div className="d-flex flex-column align-items-center align-self-stretch gap-1">
                    <h1 className={`margin-0 color-gray-700 display-sm-bold text-center ${classes["public-provider-page-main-title"]}`}>{provider.name}</h1>
                    <div className="d-flex flex-row gap-1 align-items-center justify-content-center w-100 text-center">
                        <CustomSVG className="circle-stroke-gray-800 stroke-gray-800 flex-shrink-0" SvgFunction={MapPin} height={16} width={16} />
                        <p className="margin-0 color-gray-800 text-sm-regular text-truncate">{(provider.address && (provider.address.city || provider.address.zip || provider.address.street)) ? `${provider.address.zip ? provider.address.zip : ""} ${provider.address.city ? `${provider.address.city},` : ""} ${provider.address.street ? provider.address.street : ""}` : "-"}</p>
                    </div>
                </div>
            </div>
            {!is_preview && !deleted && <PublicProviderPageFavourite provider={provider} showContactMobile={openContactMobile} />}
        </div>
    );

    if (is_preview) return content;

    return <div className={`${classes["provider-page-sticky-header"]} ${(!isMobile && isScrolled > 50) || showCompact ? classes["scrolled"] : ""}`}>
        {isMobile && <div className={classes["compact-sticky-header"]} style={{
            opacity: showCompact ? 1 : 0,
            pointerEvents: showCompact ? 'auto' : 'none',
            position: 'fixed',
            top: 'var(--header-height-sm, 50px)',
            left: 0,
            zIndex: 100,
            width: '100%'
        }}>
            <div className={classes["compact-identity"]} title={`${provider.name}${provider.address?.city ? ` (${provider.address.city})` : ''}`}>
                <h2 className={classes["compact-provider-name"]}>{provider.name}</h2>
                {provider.address?.city && <>
                    <span className={classes["compact-city-parenthesis"]}>(</span>
                    <span className={classes["compact-provider-city"]}>{provider.address.city}</span>
                    <span className={classes["compact-city-parenthesis"]}>)</span>
                </>}
            </div>
            <div className={classes["compact-actions"]}>
                <PublicProviderPageFavourite provider={provider} showContactMobile={openContactMobile} compact />
            </div>
        </div>}
        <div style={{
            opacity: 1,
            pointerEvents: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
            width: '100%'
        }} className={classes["provider-page-sticky-header-content"]}>
            {content}
            <div className={classes["public-provider-page-divider"]} />
        </div>
    </div>;
}
